import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { track } from "../lib/analytics";
import { APP_URL } from "../lib/config";

export type PosterStyle = "poster_a4" | "card_a6" | "sticker";

type Props = {
  tenantId: string;
  tenantName: string;
  primaryColor: string;
  logoUrl?: string | null;
  ticketsForReward: number;
  rewardLabel: string;
  instagram?: string | null;
};

const SIZES: Record<PosterStyle, { w: number; h: number; label: string }> = {
  poster_a4: { w: 1240, h: 1754, label: "Poster A4 (210×297 mm)" },
  card_a6: { w: 1240, h: 877, label: "Tarjeta A6 (210×148 mm)" },
  sticker: { w: 1000, h: 1000, label: "Sticker cuadrado (10×10 cm)" }
};

/**
 * Genera un poster QR brandeado del tenant para imprimir.
 * Render en canvas → download PNG. Diseño 100% paramétrico (color + logo + copy).
 */
export function BrandedQrPoster({
  tenantId,
  tenantName,
  primaryColor,
  logoUrl,
  ticketsForReward,
  rewardLabel,
  instagram
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawGenRef = useRef(0);
  const [style, setStyle] = useState<PosterStyle>("poster_a4");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  const joinUrl = `${APP_URL}/?join=${encodeURIComponent(tenantId)}`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await document.fonts.ready;
        if (!cancelled) await draw();
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, tenantName, primaryColor, logoUrl, ticketsForReward, rewardLabel, instagram, style]);

  async function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("img load failed"));
      img.src = src;
    });
  }

  async function draw() {
    const gen = ++drawGenRef.current;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const { w, h } = SIZES[style];
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, w, h);

    // Fondo
    ctx.fillStyle = "#f4f5ef";
    ctx.fillRect(0, 0, w, h);

    // Si otro draw inicio mientras tanto, abortamos para evitar overlap.
    const isStale = () => gen !== drawGenRef.current;

    if (style === "sticker") {
      drawSticker(ctx, w, h);
      if (isStale()) return;
      await drawQr(ctx, joinUrl, w * 0.5, h * 0.55, w * 0.6);
      if (isStale()) return;
      drawText(ctx, "ESCANEA", w * 0.5, h * 0.92, { size: 36, color: primaryColor, weight: 700, align: "center", spacing: 6 });
    } else if (style === "card_a6") {
      await drawHorizontalCard(ctx, w, h, isStale);
    } else {
      await drawA4Poster(ctx, w, h, isStale);
    }
  }

  function drawSticker(ctx: CanvasRenderingContext2D, w: number, h: number) {
    // Marco circular suave
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.roundRect(40, 40, w - 80, h - 80, 60);
    ctx.fill();
    drawText(ctx, tenantName.toUpperCase(), w * 0.5, h * 0.15, {
      size: 56,
      color: "#1a280c",
      weight: 700,
      align: "center",
      serif: true
    });
    if (logoUrl) {
      /* drawn below */
    }
  }

  async function drawHorizontalCard(ctx: CanvasRenderingContext2D, w: number, h: number, isStale: () => boolean) {
    // Lado izquierdo: marca + copy. Lado derecho: QR.
    const padding = 80;
    // Bloque QR derecha
    const qrBlockW = h - padding * 2;
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.roundRect(w - qrBlockW - padding, padding, qrBlockW, qrBlockW, 40);
    ctx.fill();
    await drawQr(
      ctx,
      joinUrl,
      w - qrBlockW / 2 - padding,
      h / 2,
      qrBlockW - 100
    );
    if (isStale()) return;

    // Lado izquierdo
    const leftX = padding;
    const contentW = w - qrBlockW - padding * 3;

    // logo o color stripe
    if (logoUrl) {
      try {
        const img = await loadImage(logoUrl);
        const lh = 140;
        const lw = (img.width / img.height) * lh;
        ctx.drawImage(img, leftX, padding + 20, Math.min(lw, contentW * 0.4), lh);
      } catch {}
    } else {
      ctx.fillStyle = primaryColor;
      ctx.fillRect(leftX, padding + 40, 80, 6);
    }

    drawWrapped(ctx, tenantName, leftX, padding + 200, contentW, {
      size: 84,
      color: "#1a280c",
      weight: 700,
      serif: true,
      lineHeight: 90
    });

    drawWrapped(
      ctx,
      `Junta ${ticketsForReward} compras y llévate ${rewardLabel.toLowerCase()}`,
      leftX,
      padding + 380,
      contentW,
      { size: 36, color: "#3b5320", weight: 500, lineHeight: 48 }
    );

    drawText(ctx, "ESCANEA EL QR", leftX, h - padding - 120, {
      size: 24,
      color: primaryColor,
      weight: 700,
      spacing: 8
    });
    drawText(ctx, "y empieza a sumar puntos", leftX, h - padding - 70, {
      size: 28,
      color: "#1a280c"
    });
    if (instagram) {
      drawText(ctx, instagram, leftX, h - padding - 20, {
        size: 22,
        color: "#618728"
      });
    }
  }

  async function drawA4Poster(ctx: CanvasRenderingContext2D, w: number, h: number, isStale: () => boolean) {
    // Stripe color arriba
    ctx.fillStyle = primaryColor;
    ctx.fillRect(0, 0, w, 12);

    // Marco general
    const padding = 80;
    let y = padding + 40;

    // Logo o ícono
    if (logoUrl) {
      try {
        const img = await loadImage(logoUrl);
        const lh = 180;
        const lw = (img.width / img.height) * lh;
        ctx.drawImage(img, w / 2 - lw / 2, y, lw, lh);
        y += lh + 30;
      } catch {
        y += 20;
      }
    } else {
      // Iniciales del tenant en círculo de color
      ctx.fillStyle = primaryColor;
      ctx.beginPath();
      ctx.arc(w / 2, y + 80, 80, 0, Math.PI * 2);
      ctx.fill();
      drawText(ctx, tenantName.charAt(0).toUpperCase(), w / 2, y + 105, {
        size: 80,
        color: "#fff",
        weight: 700,
        align: "center",
        serif: true
      });
      y += 200;
    }

    // Nombre del comercio
    drawWrappedCentered(ctx, tenantName, w / 2, y, w - padding * 2, {
      size: 84,
      color: "#1a280c",
      weight: 700,
      serif: true,
      lineHeight: 96
    });
    y += 130;

    // Pill "FIDELIZACIÓN"
    drawPill(ctx, "PROGRAMA DE FIDELIZACIÓN", w / 2, y, primaryColor);
    y += 90;

    // QR
    const qrSize = Math.min(700, w - padding * 4);
    const qrY = y;
    // Fondo blanco con border
    ctx.fillStyle = "#ffffff";
    ctx.strokeStyle = primaryColor + "33";
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.roundRect(w / 2 - qrSize / 2 - 30, qrY - 30, qrSize + 60, qrSize + 60, 40);
    ctx.fill();
    ctx.stroke();
    await drawQr(ctx, joinUrl, w / 2, qrY + qrSize / 2, qrSize);
    if (isStale()) return;
    y = qrY + qrSize + 80;

    // CTA
    drawText(ctx, "ESCANEA CON TU CÁMARA", w / 2, y, {
      size: 36,
      color: primaryColor,
      weight: 700,
      align: "center",
      spacing: 8
    });
    y += 70;

    // Loyalty rule
    drawWrappedCentered(
      ctx,
      `Junta ${ticketsForReward} compras y llévate ${rewardLabel.toLowerCase()}`,
      w / 2,
      y,
      w - padding * 2,
      { size: 44, color: "#1a280c", weight: 500, serif: true, lineHeight: 56 }
    );
    y += 140;

    // Footer
    const footerY = h - padding - 40;
    ctx.strokeStyle = "#ecf3d8";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(padding, footerY - 50);
    ctx.lineTo(w - padding, footerY - 50);
    ctx.stroke();

    const footerLine = instagram
      ? `Sigue ${instagram} · Con tecnología de Fideliza`
      : `Con tecnología de Fideliza`;
    drawText(ctx, footerLine, w / 2, footerY, {
      size: 22,
      color: "#618728",
      align: "center"
    });
  }

  async function drawQr(
    ctx: CanvasRenderingContext2D,
    url: string,
    cx: number,
    cy: number,
    size: number
  ) {
    const qrCanvas = document.createElement("canvas");
    await QRCode.toCanvas(qrCanvas, url, {
      width: size,
      margin: 1,
      errorCorrectionLevel: "H",
      color: { dark: "#1a280c", light: "#ffffff" }
    });
    ctx.drawImage(qrCanvas, cx - size / 2, cy - size / 2);
  }

  function drawText(
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    opts: {
      size: number;
      color: string;
      weight?: number;
      align?: CanvasTextAlign;
      serif?: boolean;
      spacing?: number;
    }
  ) {
    ctx.fillStyle = opts.color;
    const family = opts.serif ? `'Playfair Display', Georgia, serif` : `'DM Sans', sans-serif`;
    ctx.font = `${opts.weight ?? 400} ${opts.size}px ${family}`;
    ctx.textAlign = opts.align ?? "left";
    ctx.textBaseline = "alphabetic";
    if (opts.spacing) {
      const chars = text.split("");
      const w = chars.reduce((acc, c) => acc + ctx.measureText(c).width + opts.spacing!, 0) - opts.spacing!;
      const startX = opts.align === "center" ? x - w / 2 : x;
      let cx = startX;
      for (const c of chars) {
        ctx.fillText(c, cx, y);
        cx += ctx.measureText(c).width + opts.spacing;
      }
    } else {
      ctx.fillText(text, x, y);
    }
  }

  function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
    const words = text.split(" ");
    const lines: string[] = [];
    let current = "";
    for (const w of words) {
      const test = current ? current + " " + w : w;
      if (ctx.measureText(test).width > maxWidth && current) {
        lines.push(current);
        current = w;
      } else {
        current = test;
      }
    }
    if (current) lines.push(current);
    return lines;
  }

  function drawWrapped(
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    opts: { size: number; color: string; weight?: number; serif?: boolean; lineHeight: number }
  ) {
    const family = opts.serif ? `'Playfair Display', Georgia, serif` : `'DM Sans', sans-serif`;
    ctx.font = `${opts.weight ?? 400} ${opts.size}px ${family}`;
    ctx.fillStyle = opts.color;
    ctx.textAlign = "left";
    const lines = wrapLines(ctx, text, maxWidth);
    let cy = y + opts.size;
    for (const line of lines) {
      ctx.fillText(line, x, cy);
      cy += opts.lineHeight;
    }
  }

  function drawWrappedCentered(
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    opts: { size: number; color: string; weight?: number; serif?: boolean; lineHeight: number }
  ) {
    const family = opts.serif ? `'Playfair Display', Georgia, serif` : `'DM Sans', sans-serif`;
    ctx.font = `${opts.weight ?? 400} ${opts.size}px ${family}`;
    ctx.fillStyle = opts.color;
    ctx.textAlign = "center";
    const lines = wrapLines(ctx, text, maxWidth);
    let cy = y + opts.size;
    for (const line of lines) {
      ctx.fillText(line, x, cy);
      cy += opts.lineHeight;
    }
  }

  function drawPill(
    ctx: CanvasRenderingContext2D,
    text: string,
    cx: number,
    cy: number,
    color: string
  ) {
    ctx.font = `700 22px 'DM Sans', sans-serif`;
    const w = ctx.measureText(text).width;
    const padX = 24;
    const padY = 14;
    const totalW = w + padX * 2;
    const totalH = 22 + padY * 2;
    ctx.fillStyle = color + "22";
    ctx.beginPath();
    ctx.roundRect(cx - totalW / 2, cy - totalH / 2, totalW, totalH, totalH / 2);
    ctx.fill();
    ctx.fillStyle = color;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, cx, cy);
    ctx.textBaseline = "alphabetic";
  }

  async function downloadPng() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setBusy(true);
    setFeedback(null);
    try {
      await draw();
      track("qr_poster_downloaded", { tenant_id: tenantId, style });
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `fideliza-${tenantId}-${style}.png`;
        a.click();
        URL.revokeObjectURL(url);
        setFeedback("✓ Descargado");
        setTimeout(() => setFeedback(null), 2500);
      }, "image/png");
    } catch (err) {
      setFeedback(`✗ ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function printPoster() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    track("qr_poster_printed", { tenant_id: tenantId, style });
    await draw();
    const dataUrl = canvas.toDataURL("image/png");
    const win = window.open("", "_blank");
    if (!win) {
      setFeedback("Tu navegador bloqueó la ventana de impresión.");
      return;
    }
    // Construcción del documento via DOM API (sin document.write, evita XSS).
    const doc = win.document;
    doc.title = `Fideliza · ${tenantName}`;
    const styleEl = doc.createElement("style");
    styleEl.textContent = `
      @page { margin: 0; }
      html, body { margin: 0; padding: 0; }
      img { display: block; max-width: 100%; height: auto; }
      @media print { img { width: 100%; } }
    `;
    doc.head.appendChild(styleEl);
    const img = doc.createElement("img");
    img.alt = "QR";
    img.addEventListener("load", () => {
      win.print();
      win.close();
    });
    img.src = dataUrl;
    doc.body.appendChild(img);
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        {(Object.keys(SIZES) as PosterStyle[]).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStyle(s)}
            className={`rounded-2xl border-2 px-3 py-2 text-xs font-medium transition ${
              style === s
                ? "border-leaf-700 bg-leaf-700 text-white"
                : "border-leaf-200 bg-white text-leaf-700 hover:bg-leaf-50"
            }`}
          >
            {SIZES[s].label}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-2xl bg-leaf-50 p-3 ring-1 ring-leaf-100">
        <canvas
          ref={canvasRef}
          className="block w-full"
          style={{ maxWidth: "100%", height: "auto", aspectRatio: `${SIZES[style].w}/${SIZES[style].h}` }}
        />
      </div>

      <div className="flex flex-col gap-2">
        <button onClick={downloadPng} disabled={busy} className="btn-primary">
          {busy ? "Generando…" : "Descargar PNG (alta resolución)"}
        </button>
        <button onClick={printPoster} disabled={busy} className="btn-secondary">
          Imprimir directo
        </button>
      </div>

      {feedback && (
        <p
          className={`rounded-xl px-3 py-2 text-xs ${
            feedback.startsWith("✓")
              ? "bg-leaf-50 text-leaf-800"
              : "bg-red-50 text-red-700"
          }`}
        >
          {feedback}
        </p>
      )}
    </div>
  );
}
